// My audio (MacLibrary, MyAudio): its halves, the links to them, and the
// words its rows and bars say. Pure functions, tested in
// tests/w3b-myAudio.test.mjs and tests/crossLane.test.mjs. Ported from the
// app's lib/screens/my_audio_screen.dart.
import { saleTerms, usd } from "./selling.js";
import { prettySessionName } from "./sessionName.js";

// The three halves, addressable from a link: a checkout that says the
// purchase "is in My audio, Purchased" opens that tab (?tab=purchased).
export const MY_AUDIO_TABS = ["created", "purchased", "favorites"];

export const TAB_LABELS = {
  created: "Created",
  purchased: "Purchased",
  favorites: "Favorites",
};

// The tab a My audio route asks for: "created" when it names none (or one
// that doesn't exist). The old "mine" reads as "created".
export function myAudioTab(query) {
  const t = query && query.tab;
  const tab = Array.isArray(t) ? t[0] : t;
  if (tab === "mine") return "created";
  return MY_AUDIO_TABS.includes(tab) ? tab : "created";
}

// The tab a pane route shows: the sidebar's Favorites is its own place.
export function tabForRoute(route) {
  const r = route || {};
  if (r.meta && r.meta.place === "favorites") return "favorites";
  return myAudioTab(r.query);
}

// The route to a tab: Created is the plain page, Favorites its own place.
export function myAudioRoute(tab = "created") {
  const t = tab === "mine" ? "created" : tab;
  if (t === "favorites") return { path: "/library/favorites" };
  if (t === "purchased")
    return { path: "/library/my-audio", query: { tab: "purchased" } };
  return { path: "/library/my-audio" };
}

// Where "Open my audio" goes after a purchase or a claim.
export const MY_PURCHASES = myAudioRoute("purchased");

// A My audio row's licence chip, by licence (the old card's chip; kept as
// the source of tests/w1-soundCases.mjs). A paid sound you hold a licence
// for (bought, claimed, or with a pack: its row carries a download token) is
// "Owned", as the pack page and the checkout say; your own and anyone
// else's keep the licence and its starting price.
export function myAudioLicense(loop, me) {
  const l = loop || {};
  const md = l.loop_metadata || {};
  const lt = md.license_type || "CC0";
  if (lt !== "rights-reserved") return { label: lt, kind: "open" };
  const creator = l.creator || md.creator || "";
  if (l.download_token && creator !== me)
    return { label: "Owned", kind: "owned" };
  // Decision 15: rights reserved with no price is "not for sale", never
  // free; "name your price" is only a for-sale sound with a $0 minimum.
  const t = saleTerms(md);
  if (!t.for_sale)
    return { label: "Rights reserved · not for sale", kind: "muted" };
  const floor = t.checkout_min_usd || 0;
  return {
    label:
      floor > 0
        ? `Rights reserved · from ${usd(floor)}`
        : "Rights reserved · name your price",
    kind: "paid",
  };
}

// "1,284": a count with thousands separators (the app's thousands()).
export function thousands(n) {
  const v = Math.round(Number(n) || 0);
  const digits = String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return v < 0 ? `-${digits}` : digits;
}

// "Created · 865": the tab and how many it holds, once loaded.
export function tabLabel(tab, count) {
  const label = TAB_LABELS[tab] || tab;
  return count == null ? label : `${label} · ${thousands(count)}`;
}

// "865 sounds", "1 sound".
export function countLabel(n) {
  const v = Math.max(0, Math.round(Number(n) || 0));
  return `${thousands(v)} ${v === 1 ? "sound" : "sounds"}`;
}

// A person as a row names them: the display name, else the username with
// its first letter capitalised.
export function creatorLabel(sound) {
  const s = sound || {};
  if (s.creatorDisplay) return s.creatorDisplay;
  const u = String(s.creator || "");
  return u ? u.charAt(0).toUpperCase() + u.slice(1) : "";
}

// Display names by username, from rows that carry one (Purchased names its
// creators) and the signed-in account ({ username, name }).
export function knownDisplayNames(rows, account = null) {
  const names = new Map();
  for (const s of rows || []) {
    if (s && s.creator && s.creatorDisplay && !names.has(s.creator))
      names.set(s.creator, s.creatorDisplay);
  }
  const a = account || {};
  if (a.username && a.name && a.name !== a.username && !names.has(a.username))
    names.set(a.username, a.name);
  return names;
}

// /favorite_loops sends no display names (B5): a row without one borrows
// the name the rest of My audio knows for its creator.
export function withDisplayNames(sounds, names) {
  const list = sounds || [];
  if (!names || !names.size) return list;
  return list.map((s) =>
    s && !s.creatorDisplay && s.creator && names.has(s.creator)
      ? { ...s, creatorDisplay: names.get(s.creator) }
      : s,
  );
}

// 8.27 s -> "0:08".
function clock(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// Where a sound was played, as a row says it ("Friday jam", "Oct 17,
// 4:46 PM" for an app-named session).
export function soundWhere(sound) {
  const s = sound || {};
  return s.sessionDisplay || prettySessionName(s.sessionName || "") || "";
}

// A row's sub line (MacLibrary, MyAudio): your own sounds drop your name
// ("in Friday jam · 0:08", "One-shot · 0:01", "… · in Dorian Sketches" for
// the pack it is in); other people's lead with theirs ("Maya · in
// Coordinates · 0:08").
export function soundSubline(sound, { mine = false } = {}) {
  const s = sound || {};
  const parts = [];
  if (!mine) {
    const who = creatorLabel(s);
    if (who) parts.push(who);
  }
  const where = soundWhere(s);
  if (where) parts.push(`in ${where}`);
  else if (s.kind === "one_shot") parts.push("One-shot");
  if (Number(s.duration) > 0) parts.push(clock(s.duration));
  if (mine && Array.isArray(s.packs) && s.packs.length)
    parts.push(`in ${s.packs[0].title}`);
  return parts.join(" · ");
}

// Free or owned: ready to use (download, drag).
export function canTake(sound) {
  return !!sound && (!!sound.isFree || !!sound.isOwned);
}

// For sale and not yours yet.
export function canBuySound(sound) {
  return !!sound && !!sound.forSale && !sound.isOwned && !!sound.price;
}

// Favorites in three parts (MyAudio board): ready to use, purchase later,
// not for sale. Sounds you bought count as owned (purchasedHashes), and
// read "Owned" (Favorites can load before Purchased does).
export function favoriteSections(sounds, purchasedHashes = null) {
  const owned =
    purchasedHashes instanceof Set
      ? purchasedHashes
      : new Set(purchasedHashes || []);
  const all = (sounds || []).map((s) =>
    owned.has(s.hash) && !s.isOwned
      ? { ...s, isOwned: true, chip: { kind: "owned", label: "Owned" } }
      : s,
  );
  return {
    ready: all.filter((s) => canTake(s)),
    later: all.filter((s) => !canTake(s) && canBuySound(s)),
    notForSale: all.filter((s) => !canTake(s) && !canBuySound(s)),
  };
}

export const PURCHASE_LATER_NOTE =
  "Favorites you haven’t bought yet. Buying gets you the full-quality WAV and lets you duplicate sessions that use it.";

// The copy each half shows when it has nothing, can't load, or is offline.
export const EMPTY_STATES = {
  created: {
    title: "Nothing created yet",
    body: "Record or upload a loop and it shows up here.",
  },
  purchased: {
    title: "No purchases yet",
    body: "Sounds and packs you buy land here, ready to download.",
  },
  favorites: {
    title: "No favorites yet",
    body: "Star any sound, including ones you have to buy.",
  },
};
export const OFFLINE_TEXT =
  "You're offline. Your audio shows up when you reconnect.";
export function errorText(tab) {
  return `Couldn't load ${(TAB_LABELS[tab] || "your").toLowerCase()} sounds.`;
}

// The app's search over a half: title, creator, the session, and tags
// ("#lofi" matches the tag "lofi").
export function matchesQuery(sound, query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const s = sound || {};
  const tag = q.replace(/#/g, "");
  return (
    String(s.title || "")
      .toLowerCase()
      .includes(q) ||
    String(s.creator || "")
      .toLowerCase()
      .includes(q) ||
    creatorLabel(s).toLowerCase().includes(q) ||
    soundWhere(s).toLowerCase().includes(q) ||
    String(s.sessionName || "")
      .toLowerCase()
      .includes(q) ||
    (Array.isArray(s.tags) &&
      s.tags.some((t) => String(t).toLowerCase().includes(tag)))
  );
}

// All | Loops | One-shots.
export function matchesKind(sound, kind) {
  if (!kind || kind === "all") return true;
  return (sound && sound.kind) === kind;
}

// What the selection bar offers: Set price… and Make pack… only when every
// picked sound is yours; Download for the ones you may take.
export function selectionActions(sounds, me) {
  const list = sounds || [];
  const allMine =
    list.length > 0 && !!me && list.every((s) => s && s.creator === me);
  return { allMine, downloadable: list.filter((s) => canTake(s)) };
}

// "3 selected".
export function selectedLabel(n) {
  return `${Math.max(0, Number(n) || 0)} selected`;
}

// The Sales row: "4 sales · $13.00" (completed sales and their gross).
export function salesLine(totals) {
  const t = totals || {};
  const n = Number(t.completed_count ?? t.count ?? 0) || 0;
  return `${n} ${n === 1 ? "sale" : "sales"} · ${usd(t.gross_usd || 0)}`;
}

// The Sales sheet's headline: "4 completed · $13.00 gross".
export function salesHeadline(totals) {
  const t = totals || {};
  const n = Number(t.completed_count ?? 0) || 0;
  return `${n} completed · ${usd(t.gross_usd || 0)} gross`;
}

// A sale row's second line: the buyer, and the status while not complete.
export function saleSubline(sale) {
  const s = sale || {};
  const who =
    s.buyer_display_name ||
    (s.buyer ? s.buyer.charAt(0).toUpperCase() + s.buyer.slice(1) : "");
  const parts = [];
  if (who) parts.push(who);
  if (s.status && s.status !== "completed")
    parts.push(s.status.charAt(0).toUpperCase() + s.status.slice(1));
  return parts.join(" · ");
}

// A Sound with Set price's answer applied (PUT /sounds/<h>/pricing's
// pricing view): the licence, the terms, and both chips again.
export function applyPricing(sound, view) {
  const s = sound || {};
  const v = view || {};
  const forSale = !!v.for_sale;
  const isFree = !!v.is_free;
  const num = (x) =>
    x == null || x === "" || !Number.isFinite(Number(x)) ? null : Number(x);
  const price = forSale
    ? {
        minUsd: num(v.min_usd) ?? 0,
        suggestedUsd: num(v.suggested_usd),
        checkoutMinUsd: num(v.checkout_min_usd) ?? num(v.min_usd) ?? 0,
      }
    : null;
  const ownerChip = isFree
    ? { kind: "free", label: "Free" }
    : forSale
      ? { kind: "price", label: usd(price.checkoutMinUsd) }
      : { kind: "notForSale", label: "Not for sale" };
  const out = {
    ...s,
    licenseType: v.license_type || s.licenseType,
    licenseState:
      v.license_state ||
      (isFree ? "open" : forSale ? "for_sale" : "not_for_sale"),
    forSale,
    isFree,
    price,
    ownerChip,
  };
  if ("require_attribution" in v)
    out.requireAttribution = !!v.require_attribution;
  if ("allow_commercial" in v)
    out.allowCommercial = v.allow_commercial !== false;
  // Your own sound stays "Owned" to you; anyone else's chip follows the
  // new terms.
  if (!s.mine)
    out.chip = s.isOwned ? { kind: "owned", label: "Owned" } : ownerChip;
  return out;
}

// The licence a ready-to-use row names ("CC0", "CC BY"), or null.
export function openLicenseLabel(sound) {
  const lt = String((sound && sound.licenseType) || "");
  if (!sound || !sound.isFree || !lt) return null;
  if (lt.toUpperCase() === "CC0") return "CC0";
  return lt.replace(/^CC-/i, "CC ");
}

// A Purchased or Favorites row's chip, the same in the Mac table and the
// cards (the MyAudio board): an open licence by name ("CC0"), your own
// price on your own sound (never "Owned"), else what it costs you ("Owned",
// "$2.99", "Not for sale").
export function libraryChip(sound) {
  const s = sound || {};
  const open = openLicenseLabel(s);
  if (open) return { kind: "free", label: open };
  const chip = s.mine ? s.ownerChip : s.chip;
  return chip || { kind: "notForSale", label: "Not for sale" };
}
